import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { RootState, useAppDispatch } from '../../../../store';
import { Stage, StageFormData, defaultStageFormData, STAGE_CATEGORIES, STAGE_STATUSES } from '../../types/stage';
import { createStage, updateStage, fetchStages } from '../../store/stageSlice';

interface StageFormProps {
  isOpen: boolean;
  onClose: () => void;
  stage?: Stage | null;
  mode: 'create' | 'edit';
}

const StageForm: React.FC<StageFormProps> = ({ isOpen, onClose, stage, mode }) => {
  const dispatch = useAppDispatch();
  const { stages, loading } = useSelector((state: RootState) => state.stages);
  
  const [formData, setFormData] = useState<StageFormData>(defaultStageFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Helper function to check if a stage is a child of another stage
  const isChildStage = (stageId: string, parentId?: string): boolean => {
    if (!parentId) return false;
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return false;
    
    let currentParent = stage.parent_stage_id;
    while (currentParent) {
      if (currentParent === parentId) return true;
      const parent = stages.find(s => s.id === currentParent);
      currentParent = parent?.parent_stage_id;
    }
    return false;
  };

  // Update form data when stage prop changes
  useEffect(() => {
    if (stage && mode === 'edit') {
      const stageFormData: StageFormData = {
        name: stage.name || '',
        description: stage.description || '',
        category: stage.category || '',
        parent_stage_id: stage.parent_stage_id || '',
        is_parallel: stage.is_parallel || false,
        estimated_duration_minutes: stage.estimated_duration_minutes || 0,
        skill_ids: stage.required_skills || [],
        status: stage.status || 'active'
      };
      setFormData(stageFormData);
    } else {
      setFormData(defaultStageFormData);
    }
    setErrors({});
  }, [stage, mode]);

  // Get available parent stages (exclude current stage and its children in edit mode)
  const availableParentStages = stages.filter(s => 
    s.status === 'active' && 
    (mode === 'create' || s.id !== stage?.id) &&
    !isChildStage(s.id, stage?.id)
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    // Real-time validation for stage name
    if (name === 'name' && value.trim()) {
      const trimmedName = value.trim();
      const duplicateStage = stages.find(s => 
        s.name.toLowerCase() === trimmedName.toLowerCase() &&
        s.status === 'active' &&
        (mode === 'create' || s.id !== stage?.id)
      );
      
      if (duplicateStage) {
        setErrors(prev => ({ ...prev, name: 'A stage with this name already exists' }));
      } else {
        setErrors(prev => ({ ...prev, name: '' }));
      }
    } else {
      // Clear error when field is edited
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Stage name is required';
    } else {
      // Check for duplicate stage names (case-insensitive)
      const trimmedName = formData.name.trim();
      const duplicateStage = stages.find(s => 
        s.name.toLowerCase() === trimmedName.toLowerCase() &&
        s.status === 'active' &&
        (mode === 'create' || s.id !== stage?.id) // Exclude current stage in edit mode
      );
      
      if (duplicateStage) {
        newErrors.name = 'A stage with this name already exists';
      }
    }
    
    if (formData.estimated_duration_minutes < 0) {
      newErrors.estimated_duration_minutes = 'Duration must be positive';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      const stageData = {
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category || undefined,
        parent_stage_id: formData.parent_stage_id || undefined,
        is_parallel: formData.is_parallel,
        estimated_duration_minutes: formData.estimated_duration_minutes || undefined,
        skill_ids: formData.skill_ids.length > 0 ? formData.skill_ids : undefined,
        status: formData.status
      };

      if (mode === 'create') {
        await dispatch(createStage(stageData)).unwrap();
        // Refresh the stages list after successful creation
        dispatch(fetchStages());
      } else if (stage) {
        await dispatch(updateStage({ id: stage.id, stageData })).unwrap();
        // Refresh the stages list after successful update
        dispatch(fetchStages());
      }
      
      onClose();
    } catch (error: any) {
      console.error('Error saving stage:', error);
      console.error('Error details:', {
        message: error?.message || error || 'Unknown error',
        stack: error?.stack,
        name: error?.name
      });
      // Show user-friendly error message
      const errorMessage = error?.message || 
                          (typeof error === 'string' ? error : 'Failed to save stage. Please try again.');
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(defaultStageFormData);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={handleClose} />
        
        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {mode === 'create' ? 'Create New Stage' : 'Edit Stage'}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* General Error Display */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  {errors.general}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Stage Name */}
                <div className="md:col-span-2">
                  <TextField
                    fullWidth
                    required
                    name="name"
                    label="Stage Name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    placeholder="Enter stage name"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <TextField
                    fullWidth
                    name="description"
                    label="Description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Enter stage description"
                  />
                </div>

                {/* Category */}
                <div>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      label="Category"
                    >
                      <MenuItem value="">Select category</MenuItem>
                      {STAGE_CATEGORIES.map(category => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Parent Stage */}
                <div>
                  <FormControl fullWidth>
                    <InputLabel>Parent Stage</InputLabel>
                    <Select
                      name="parent_stage_id"
                      value={formData.parent_stage_id}
                      onChange={handleChange}
                      label="Parent Stage"
                    >
                      <MenuItem value="">No parent (Root stage)</MenuItem>
                      {availableParentStages.map(parentStage => (
                        <MenuItem key={parentStage.id} value={parentStage.id}>
                          {parentStage.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Estimated Duration */}
                <div>
                  <TextField
                    fullWidth
                    name="estimated_duration_minutes"
                    label="Estimated Duration (minutes)"
                    type="number"
                    value={formData.estimated_duration_minutes}
                    onChange={handleChange}
                    error={!!errors.estimated_duration_minutes}
                    helperText={errors.estimated_duration_minutes}
                    inputProps={{ min: 0 }}
                    placeholder="0"
                  />
                </div>

                {/* Status */}
                <div>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      label="Status"
                    >
                      {STAGE_STATUSES.map(status => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Parallel Processing */}
                <div className="md:col-span-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="is_parallel"
                        checked={formData.is_parallel}
                        onChange={handleChange}
                      />
                    }
                    label="Can run in parallel with other stages"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Stage' : 'Update Stage'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StageForm;
